import { type ReactNode, useState } from 'react';
import { ActionIcon, Kbd, Menu, Tooltip } from '@mantine/core';
import { IconDotsVertical, IconKeyboard, IconLanguage, IconSettings } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LANGUAGES } from '@/i18n';

export function MoreMenu({
    onOpenSettings,
    onOpenShortcuts,
    children,
}: Readonly<{ onOpenSettings?: () => void; onOpenShortcuts: () => void; children?: ReactNode }>) {
    const { t, i18n } = useTranslation('conference');
    const { t: tCommon } = useTranslation('common');
    const currentLanguage = SUPPORTED_LANGUAGES.find((language) => language === i18n.resolvedLanguage) ?? 'ru';
    const [opened, setOpened] = useState(false);

    return (
        <Menu withinPortal opened={opened} position="top-end" onChange={setOpened}>
            <Menu.Target>
                <Tooltip disabled={opened} label={t('controls.more')}>
                    <ActionIcon aria-label={t('controls.more')} radius="var(--radius-rounded-full)" size="lg" variant="default">
                        <IconDotsVertical size={24} />
                    </ActionIcon>
                </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
                {children}
                <Menu.Sub position="left-start">
                    <Menu.Sub.Target>
                        <Menu.Sub.Item leftSection={<IconLanguage size={16} />}>{t('controls.language')}</Menu.Sub.Item>
                    </Menu.Sub.Target>
                    <Menu.Sub.Dropdown>
                        <Menu.RadioGroup value={currentLanguage} onChange={(language) => void i18n.changeLanguage(language)}>
                            {SUPPORTED_LANGUAGES.map((language) => (
                                <Menu.RadioItem key={language} closeMenuOnClick value={language}>
                                    {tCommon(`language.${language}`)}
                                </Menu.RadioItem>
                            ))}
                        </Menu.RadioGroup>
                    </Menu.Sub.Dropdown>
                </Menu.Sub>
                <Menu.Item
                    leftSection={<IconKeyboard size={16} />}
                    rightSection={<Kbd size="xs">?</Kbd>}
                    onClick={onOpenShortcuts}
                >
                    {t('shortcuts.title')}
                </Menu.Item>
                {onOpenSettings && (
                    <Menu.Item leftSection={<IconSettings size={16} />} onClick={onOpenSettings}>
                        {t('controls.settings')}
                    </Menu.Item>
                )}
            </Menu.Dropdown>
        </Menu>
    );
}
